var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { scrollIntoView } from '../tree/scrollIntoView';
import { useRenderers } from '../renderers/useRenderers';
import { buildMapForTrees } from '../utils';
import { getItemsLinearly } from '../tree/getItemsLinearly';
import { useRefCopy } from '../useRefCopy';
import { useStableHandler } from '../use-stable-handler';
export var useControlledTreeEnvironmentProps = function (_a) {
    var onExpandItemProp = _a.onExpandItem, onCollapseProp = _a.onCollapseItem, onDropProp = _a.onDrop, props = __rest(_a, ["onExpandItem", "onCollapseItem", "onDrop"]);
    var _b = useState({}), trees = _b[0], setTrees = _b[1];
    var _c = useState({}), linearItems = _c[0], setLinearItems = _c[1];
    var _d = useState(), activeTreeId = _d[0], setActiveTreeId = _d[1];
    var viewStateRef = useRefCopy(props.viewState);
    var treeIds = useMemo(function () { return Object.keys(trees); }, [trees]);
    var onFocusItem = props.onFocusItem, autoFocus = props.autoFocus, onRegisterTree = props.onRegisterTree, onUnregisterTree = props.onUnregisterTree, items = props.items;
    var onFocusItemRef = useRefCopy(onFocusItem);
    var updateLinearItems = useStableHandler(function () {
        setTimeout(function () {
            setLinearItems(buildMapForTrees(treeIds, function (treeId) {
                var _a;
                return getItemsLinearly(trees[treeId].rootItem, (_a = viewStateRef.current[treeId]) !== null && _a !== void 0 ? _a : {}, items);
            }));
        });
    });
    useEffect(function () { return updateLinearItems(); }, [items, treeIds, updateLinearItems]);
    var onFocusItemHandler = useCallback(function (item, treeId) {
        var _a, _b, _c, _d, _e;
        var newItem = document.querySelector("[data-rct-tree=\"".concat(treeId, "\"] [data-rct-item-id=\"").concat(item.index, "\"]"));
        if (autoFocus !== null && autoFocus !== void 0 ? autoFocus : true) {
            if (((_b = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.attributes.getNamedItem('data-rct-search-input')) === null || _b === void 0 ? void 0 : _b.value) !== 'true') {
                // Move DOM focus to item if the current focus is not on the search input
                (_c = newItem === null || newItem === void 0 ? void 0 : newItem.focus) === null || _c === void 0 ? void 0 : _c.call(newItem);
            }
            else {
                // Otherwise just manually scroll into view
                scrollIntoView(newItem);
            }
        }
        if (((_d = viewStateRef.current[treeId]) === null || _d === void 0 ? void 0 : _d.focusedItem) === item.index) {
            return;
        }
        (_e = onFocusItemRef.current) === null || _e === void 0 ? void 0 : _e.call(onFocusItemRef, item, treeId);
    }, [autoFocus, onFocusItemRef, viewStateRef]);
    var registerTree = useCallback(function (tree) {
        setTrees(function (trees) {
            var _a;
            return (__assign(__assign({}, trees), (_a = {}, _a[tree.treeId] = tree, _a)));
        });
        onRegisterTree === null || onRegisterTree === void 0 ? void 0 : onRegisterTree(tree);
        updateLinearItems();
    }, [onRegisterTree, updateLinearItems]);
    var unregisterTree = useCallback(function (treeId) {
        onUnregisterTree === null || onUnregisterTree === void 0 ? void 0 : onUnregisterTree(trees[treeId]);
        delete trees[treeId];
        setTrees(trees);
    }, [onUnregisterTree, trees]);
    var onCollapseItem = useCallback(function (item, treeId) {
        onCollapseProp === null || onCollapseProp === void 0 ? void 0 : onCollapseProp(item, treeId);
        updateLinearItems();
    }, [onCollapseProp, updateLinearItems]);
    var onExpandItem = useCallback(function (item, treeId) {
        onExpandItemProp === null || onExpandItemProp === void 0 ? void 0 : onExpandItemProp(item, treeId);
        updateLinearItems();
    }, [onExpandItemProp, updateLinearItems]);
    var onDrop = useCallback(function (items, target) {
        onDropProp === null || onDropProp === void 0 ? void 0 : onDropProp(items, target);
        updateLinearItems();
    }, [onDropProp, updateLinearItems]);
    var focusTree = useCallback(function (treeId) {
        var _a;
        var focusItem = document.querySelector("[data-rct-tree=\"".concat(treeId, "\"] [data-rct-item-focus=\"true\"]"));
        (_a = focusItem === null || focusItem === void 0 ? void 0 : focusItem.focus) === null || _a === void 0 ? void 0 : _a.call(focusItem);
    }, []);
    var setActiveTree = useCallback(function (treeIdOrSetStateFunction, autoFocusTree) {
        if (autoFocusTree === void 0) { autoFocusTree = true; }
        var maybeFocusTree = function (treeId) {
            var _a;
            if (autoFocusTree &&
                (autoFocus !== null && autoFocus !== void 0 ? autoFocus : true) &&
                treeId &&
                !((_a = document
                    .querySelector("[data-rct-tree=\"".concat(treeId, "\"]"))) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
                focusTree(treeId);
            }
        };
        if (typeof treeIdOrSetStateFunction === 'function') {
            setActiveTreeId(function (oldValue) {
                var treeId = treeIdOrSetStateFunction(oldValue);
                if (treeId !== oldValue) {
                    maybeFocusTree(treeId);
                }
                return treeId;
            });
        }
        else {
            var treeId = treeIdOrSetStateFunction;
            setActiveTreeId(treeId);
            maybeFocusTree(treeId);
        }
    }, [autoFocus, focusTree]);
    var renderers = useRenderers(props);
    return __assign(__assign(__assign({}, renderers), props), { onFocusItem: onFocusItemHandler, registerTree: registerTree, unregisterTree: unregisterTree, onExpandItem: onExpandItem, onCollapseItem: onCollapseItem, onDrop: onDrop, setActiveTree: setActiveTree, treeIds: treeIds, trees: trees, activeTreeId: activeTreeId, linearItems: linearItems });
};
